import { Injectable } from '@angular/core';
import { Amplify } from 'aws-amplify';
import { signInWithRedirect, signOut, fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import { getAmplifyConfig } from '@app/config/amplify.config';
import { tenantToIDP } from '@app/config/auth-constants';
import { BehaviorSubject, Observable } from 'rxjs';

export interface AuthUser {
  userId: string;
  username: string;
  email?: string;
  tenantId?: string;
  campusId?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentUserSubject = new BehaviorSubject<AuthUser | null>(null);
  public currentUser$: Observable<AuthUser | null> = this.currentUserSubject.asObservable();

  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  public isAuthenticated$: Observable<boolean> = this.isAuthenticatedSubject.asObservable();

  constructor() {
    this.initializeAmplify();
    // Check if there's an OAuth error in the URL
    this.handleOAuthError();
  }

  /**
   * Initialize auth state - call this from provideAppInitializer
   * to ensure auth is resolved before app renders
   */
  async init(): Promise<void> {
    await this.checkAuthStatus();
  }

  private handleOAuthError(): void {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('error') === 'redirect_mismatch') {
      console.warn('OAuth redirect mismatch detected. Clearing OAuth state...');
      // Clear all Amplify-related localStorage keys
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('CognitoIdentityServiceProvider') || 
            key.includes('amplify') || 
            key.includes('oauth')) {
          localStorage.removeItem(key);
        }
      });
      // Redirect to clean URL
      window.location.href = window.location.origin;
    }
  }

  private initializeAmplify(): void {
    // Get environment from window location or default to dev
    const hostname = window.location.hostname;
    let env = 'dev';
    
    if (hostname.includes('test.')) {
      env = 'test';
    } else if (hostname.includes('compass.futurenav')) {
      env = 'prod';
    }

    Amplify.configure(getAmplifyConfig(env));
  }

  private async checkAuthStatus(): Promise<void> {
    try {
      const user = await getCurrentUser();
      //console.log('Current Cognito user:', user);
      if (user) {
        const session = await fetchAuthSession();
        const tokens = session.tokens;
        
        const authUser: AuthUser = {
          userId: tokens?.idToken?.payload['custom:userId'] as string,
          username: user.username,
          email: tokens?.idToken?.payload['email'] as string,
          tenantId: tokens?.idToken?.payload['custom:tenantId'] as string,
          campusId: tokens?.idToken?.payload['custom:campusId'] as string,
        };

        this.currentUserSubject.next(authUser);
        this.isAuthenticatedSubject.next(true);
      } else {
        this.currentUserSubject.next(null);
        this.isAuthenticatedSubject.next(false);
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
      this.currentUserSubject.next(null);
      this.isAuthenticatedSubject.next(false);
    }
  }

  async signInWithSSO(tenant?: string): Promise<void> {
    if (!tenant) {
      throw new Error('Tenant is required for SSO authentication');
    }

    const provider = tenantToIDP[tenant];
    console.log('Using SSO provider:', provider);
    
    if (!provider) {
      throw new Error(`No SSO provider configured for tenant: ${tenant}`);
    }

    // Save tenant to localStorage for retrieval after redirect
    localStorage.setItem('tenant', tenant);

    await signInWithRedirect({
      provider: {
        custom: provider,
      },
    });
  }

  async signOut(): Promise<void> {
    try {
      await signOut();
      this.currentUserSubject.next(null);
      this.isAuthenticatedSubject.next(false);
      localStorage.removeItem('tenant');
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  }

  async getAuthSession() {
    return await fetchAuthSession();
  }

  getCurrentUser(): AuthUser | null {
    return this.currentUserSubject.value;
  }

  isAuthenticated(): boolean {
    return this.isAuthenticatedSubject.value;
  }
}
